import React, { useMemo, useState } from "react";
import {
  Box,
  Center,
  Checkbox,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Select,
  Spinner,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  VStack,
  Wrap,
  WrapItem,
  Button,
} from "@chakra-ui/react";
import { AppIcons } from "constants/icons";
import { ColumnType, FilterOperator, filterOperatorToJSON } from "build-ts/enums";
import { ColumnInfo, FilterProto } from "build-ts/metadata";
import DateFilterPicker from "modules/common/DateFilterPicker";
import { DateFilter } from "build-ts/relative_date";
import { DateFilterCategory } from "utils/enum";
import { getDateFilterName } from "utils/nameUtils";
import { getOperatorSymbol } from "utils/analysisUtils";

interface FiltersProps {
  columns: ColumnInfo[];
  columnValues?: string[];
  selectedFilterColumn: ColumnInfo | null;
  selectedOperator?: FilterOperator;
  inputValue: string;
  selectedColumnValues: string[];
  selectedFilterColumns: FilterProto[];
  dateFilter: DateFilter | undefined;
  dateFilterCategory: DateFilterCategory | undefined;
  isLoading: boolean;
  compact?: boolean;

  setDateFilter: React.Dispatch<React.SetStateAction<DateFilter | undefined>>;
  setDateFilterCategory: React.Dispatch<React.SetStateAction<DateFilterCategory>>;
  handleFilterColumnChange: (columnId: string) => void;
  handleOperatorChange: (event: React.ChangeEvent<HTMLSelectElement>) => void;
  handleValueChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleAddFilterColumn: () => void;
  handleToggle: (value: string) => void;
  handleDeleteFilterColumn: (index: number) => void;
}

const Filters: React.FC<FiltersProps> = ({
  columns,
  columnValues,
  selectedFilterColumn,
  selectedOperator,
  inputValue,
  selectedColumnValues,
  selectedFilterColumns,
  dateFilter,
  dateFilterCategory,
  isLoading,
  compact,
  setDateFilter,
  setDateFilterCategory,
  handleFilterColumnChange,
  handleOperatorChange,
  handleValueChange,
  handleAddFilterColumn,
  handleToggle,
  handleDeleteFilterColumn,
}) => {
  const fs = compact ? "xs" : "sm";
  const controlSize = compact ? "sm" : "md";

  const [columnSearchTerm, setColumnSearchTerm] = useState("");
  const [valueSearchTerm, setValueSearchTerm] = useState("");

  const isAttribute = selectedFilterColumn?.column_type === ColumnType.ATTRIBUTE;
  const isTemporal = selectedFilterColumn?.column_type === ColumnType.TEMPORAL;
  const isMeasure = selectedFilterColumn?.column_type === ColumnType.MEASURE;

  const isAddDisabled =
    selectedFilterColumn === null ||
    (isAttribute
      ? selectedOperator === undefined || selectedColumnValues.length === 0
      : isTemporal
        ? !dateFilter
        : selectedOperator === undefined || !inputValue.trim());

  const filteredAllColumns = useMemo(() => {
    if (!columnSearchTerm.trim()) return columns ?? [];
    return (columns ?? []).filter(c => c.header?.name?.toLowerCase().includes(columnSearchTerm.toLowerCase()));
  }, [columns, columnSearchTerm]);

  const filteredColumnValues = useMemo(() => {
    if (!columnValues) return [];
    if (!valueSearchTerm.trim()) return columnValues;
    return columnValues.filter(option => {
      const displayValue = option === null ? "{Null}" : option.toString();
      return displayValue.toLowerCase().includes(valueSearchTerm.toLowerCase());
    });
  }, [columnValues, valueSearchTerm]);

  return (
    <HStack spacing={0} align="flex-start" w="100%">
      {/* ── Left: filter builder ── */}
      <VStack flex={1} spacing={3} align="stretch" pr={4} minW={0}>
        {/* Column selector */}
        <Menu>
          <MenuButton
            as={Button}
            width="100%"
            size={controlSize}
            rightIcon={<AppIcons.chevronDown size={12} />}
            fontSize={fs}
            bg="white"
            border="1px"
            borderColor="gray.300"
            borderRadius="md"
            fontWeight="normal"
            _hover={{ bg: "purple.50" }}
            textAlign="left"
            color={selectedFilterColumn ? "gray.800" : "gray.400"}
          >
            {selectedFilterColumn?.header?.name ?? "Select Column"}
          </MenuButton>
          <MenuList zIndex={10} p={0} overflow="hidden">
            {/* Sticky search — outside the scrollable area */}
            <Box p={2} pb={1} bg="white" borderBottomWidth="1px" borderColor="gray.100">
              <Input
                placeholder="Search columns..."
                value={columnSearchTerm}
                onChange={e => setColumnSearchTerm(e.target.value)}
                size="sm"
                borderColor="gray.300"
                _focus={{ borderColor: "purple.400" }}
                onClick={e => e.stopPropagation()}
              />
            </Box>
            {/* Scrollable items */}
            <Box maxHeight="220px" overflowY="auto">
              {filteredAllColumns.map(col => (
                <MenuItem
                  key={col.header?.id}
                  onClick={() => {
                    handleFilterColumnChange(col.header?.id ?? "");
                    setColumnSearchTerm("");
                  }}
                  fontSize={fs}
                  bg={selectedFilterColumn?.header?.id === col.header?.id ? "purple.50" : undefined}
                >
                  {col.header?.name}
                </MenuItem>
              ))}
              {filteredAllColumns.length === 0 && (
                <Box p={2} color="gray.500" fontSize={fs}>
                  No columns found
                </Box>
              )}
            </Box>
          </MenuList>
        </Menu>

        {/* Type-dependent value inputs */}
        {selectedFilterColumn &&
          (isLoading ? (
            <Center py={2}>
              <Spinner size="sm" color="purple.500" thickness="2px" />
            </Center>
          ) : isAttribute ? (
            /* Operator + multi-select values + add button — all inline */
            <HStack spacing={2} align="center">
              <Select
                onChange={handleOperatorChange}
                value={selectedOperator !== undefined ? filterOperatorToJSON(selectedOperator) : ""}
                fontSize={fs}
                size={controlSize}
                bg="white"
                borderColor="gray.300"
                flexShrink={0}
                w="72px"
              >
                <option value="EQ">=</option>
                <option value="NE">!=</option>
              </Select>
              <Menu closeOnSelect={false}>
                <MenuButton
                  as={Button}
                  flex={1}
                  size={controlSize}
                  rightIcon={<AppIcons.chevronDown size={12} />}
                  fontSize={fs}
                  bg="white"
                  border="1px"
                  borderColor="gray.300"
                  borderRadius="md"
                  fontWeight="normal"
                  _hover={{ bg: "purple.50" }}
                  textAlign="left"
                  color={selectedColumnValues.length > 0 ? "gray.800" : "gray.400"}
                  minW={0}
                  isTruncated
                >
                  {selectedColumnValues.length > 0 ? `Selected (${selectedColumnValues.length})` : "Values"}
                </MenuButton>
                <MenuList zIndex={10} p={0} overflow="hidden">
                  {/* Sticky search — outside the scrollable area */}
                  <Box p={2} pb={1} bg="white" borderBottomWidth="1px" borderColor="gray.100">
                    <Input
                      placeholder="Search..."
                      value={valueSearchTerm}
                      onChange={e => setValueSearchTerm(e.target.value)}
                      size="sm"
                      borderColor="gray.300"
                      _focus={{ borderColor: "purple.400" }}
                      onClick={e => e.stopPropagation()}
                    />
                  </Box>
                  {/* Scrollable items */}
                  <Box maxHeight="200px" overflowY="auto">
                    {filteredColumnValues.map((option, index) => (
                      <MenuItem key={index} minH="36px">
                        <Checkbox
                          isChecked={selectedColumnValues.includes(option)}
                          onChange={() => handleToggle(option)}
                          colorScheme="purple"
                        >
                          <Text fontSize={fs} m={0}>
                            {option === null ? "{Null}" : option.toString()}
                          </Text>
                        </Checkbox>
                      </MenuItem>
                    ))}
                    {filteredColumnValues.length === 0 && valueSearchTerm.trim() && (
                      <Box p={2} color="gray.500" fontSize={fs}>
                        No options found
                      </Box>
                    )}
                  </Box>
                </MenuList>
              </Menu>
              <IconButton
                aria-label="Add filter"
                icon={<AppIcons.plus size={16} />}
                colorScheme="purple"
                size={controlSize}
                onClick={handleAddFilterColumn}
                isDisabled={isAddDisabled}
                flexShrink={0}
              />
            </HStack>
          ) : isMeasure ? (
            /* Operator + free-text input + add button — all inline */
            <HStack spacing={2} align="center">
              <Select
                onChange={handleOperatorChange}
                value={selectedOperator !== undefined ? filterOperatorToJSON(selectedOperator) : ""}
                fontSize={fs}
                size={controlSize}
                bg="white"
                borderColor="gray.300"
                flexShrink={0}
                w="72px"
              >
                <option value="EQ">=</option>
                <option value="NE">!=</option>
                <option value="GT">&gt;</option>
                <option value="GTE">&gt;=</option>
                <option value="LT">&lt;</option>
                <option value="LTE">&lt;=</option>
              </Select>
              <Input
                type="text"
                placeholder="Value"
                value={inputValue}
                onChange={handleValueChange}
                fontSize={fs}
                size={controlSize}
                bg="white"
                borderColor="gray.300"
                flex={1}
                minW={0}
              />
              <IconButton
                aria-label="Add filter"
                icon={<AppIcons.plus size={16} />}
                colorScheme="purple"
                size={controlSize}
                onClick={handleAddFilterColumn}
                isDisabled={isAddDisabled}
                flexShrink={0}
              />
            </HStack>
          ) : isTemporal ? (
            /* Date picker below the column row, add button right-aligned below it */
            <VStack spacing={2} align="stretch">
              <DateFilterPicker
                setDateFilter={setDateFilter}
                dateFilter={dateFilter}
                setDateFilterCategory={setDateFilterCategory}
                dateFilterCategory={dateFilterCategory}
                id="filters-temporal"
              />
              <IconButton
                aria-label="Add filter"
                icon={<AppIcons.plus size={16} />}
                colorScheme="purple"
                size={controlSize}
                onClick={handleAddFilterColumn}
                isDisabled={isAddDisabled}
                alignSelf="flex-end"
              />
            </VStack>
          ) : null)}
      </VStack>

      {/* ── Vertical divider ── */}
      <Box w="1px" bg="gray.200" alignSelf="stretch" flexShrink={0} />

      {/* ── Right: applied filters list ── */}
      <Box flex={1} pl={4} minW={0}>
        {selectedFilterColumns.length === 0 ? (
          <Text fontSize={fs} color="gray.400" fontStyle="italic">
            No filters added
          </Text>
        ) : (
          <Wrap spacing={2}>
            {selectedFilterColumns.map((filter, index) => {
              let displayText = "";
              if (filter.date_filter) {
                displayText = `${filter.logical_column_header?.name}: ${getDateFilterName(filter.date_filter)}`;
              } else if (filter.logical_column_header && filter.values && filter.values.length > 0) {
                const symbol = getOperatorSymbol(filter.operator!);
                displayText = `${filter.logical_column_header.name} ${symbol} ${filter.values.join(", ")}`;
              }
              return displayText ? (
                <WrapItem key={index}>
                  <Tag
                    size="sm"
                    fontSize="xs"
                    bg="purple.50"
                    border="1px"
                    borderColor="purple.300"
                    color="gray.800"
                    py={1}
                  >
                    <TagLabel>{displayText}</TagLabel>
                    <TagCloseButton onClick={() => handleDeleteFilterColumn(index)} />
                  </Tag>
                </WrapItem>
              ) : null;
            })}
          </Wrap>
        )}
      </Box>
    </HStack>
  );
};

export default Filters;
